import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, MessageSquare, Award, ExternalLink, ShoppingBag } from 'lucide-react';
import { api } from '../lib/api';

export default function UserProfile() {
  const { username } = useParams<{ username: string }>();
  const [activeTab, setActiveTab] = useState<'threads' | 'posts'>('threads');

  const { data, isLoading } = useQuery({
    queryKey: ['user-profile', username],
    queryFn: () => api.get(`/forum/users/${username}`),
    enabled: !!username,
  });

  const user = (data as any)?.data?.user;
  const threads = (data as any)?.data?.threads || [];
  const posts = (data as any)?.data?.posts || [];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-gold border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-western text-3xl text-gold mb-4">User Not Found</h2>
          <p className="text-text-secondary mb-6">This user doesn't exist or has been banned.</p>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  const roleColors: Record<string, string> = {
    owner: 'text-yellow-300',
    admin: 'text-hot',
    moderator: 'text-gold',
    seller: 'text-rust-light',
    buyer: 'text-cream',
  };

  return (
    <div className="min-h-screen">
      {/* Banner */}
      <div className="relative h-48 md:h-64 bg-gradient-to-r from-leather to-charcoal overflow-hidden">
        {user.profileBannerUrl ? (
          <img
            src={user.profileBannerUrl}
            alt="Profile banner"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-leather via-charcoal to-background" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
      </div>

      {/* Profile Info */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative -mt-16 mb-6 flex flex-col sm:flex-row items-start gap-6">
          {/* Avatar */}
          <div className="relative z-10 flex-shrink-0">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.username}
                className="w-28 h-28 rounded-full object-cover border-4 border-background shadow-xl"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-leather border-4 border-background shadow-xl flex items-center justify-center">
                <span className="font-western text-4xl text-gold">
                  {user.username.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Name & Meta */}
          <div className="pt-2 sm:pt-14 flex-1">
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <h1 className="font-western text-3xl text-cream">
                {user.displayName || user.username}
              </h1>
              {user.role && user.role !== 'buyer' && (
                <span className={`text-sm font-medium capitalize px-2 py-0.5 rounded-full bg-gold/10 ${roleColors[user.role] || 'text-cream'}`}>
                  {user.role}
                </span>
              )}
            </div>
            <p className="text-text-muted mb-2">@{user.username}</p>
            {user.customTitle && (
              <p className="text-gold italic text-sm mb-2">{user.customTitle}</p>
            )}
            {user.bio && (
              <p className="text-text-secondary text-sm max-w-xl">{user.bio}</p>
            )}
          </div>

          {/* Links */}
          <div className="flex flex-wrap gap-2 sm:pt-14">
            <a
              href={`https://forum.outlawoasis.com/user/${user.username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-surface border border-gold/20 text-cream rounded-lg hover:border-gold/50 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Forum Profile
            </a>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap gap-6 mb-8 pb-6 border-b border-gold/10">
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Calendar className="w-4 h-4 text-gold" />
            <span>Joined {new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <MessageSquare className="w-4 h-4 text-gold" />
            <span>{user.forumStats?.postCount || 0} forum posts</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Award className="w-4 h-4 text-gold" />
            <span>{user.forumStats?.reputation || 0} reputation</span>
          </div>
          {(user.role === 'seller' || user.role === 'admin' || user.role === 'owner') && (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <ShoppingBag className="w-4 h-4 text-gold" />
              <span>Seller</span>
            </div>
          )}
        </div>

        {/* Forum Activity Tabs */}
        {(threads.length > 0 || posts.length > 0) && (
          <>
            <div className="flex gap-1 border-b border-gold/20 mb-6">
              <button
                onClick={() => setActiveTab('threads')}
                className={`px-4 py-3 font-medium transition-colors relative ${
                  activeTab === 'threads' ? 'text-gold' : 'text-text-secondary hover:text-cream'
                }`}
              >
                Forum Threads ({threads.length})
                {activeTab === 'threads' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />}
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`px-4 py-3 font-medium transition-colors relative ${
                  activeTab === 'posts' ? 'text-gold' : 'text-text-secondary hover:text-cream'
                }`}
              >
                Replies ({posts.length})
                {activeTab === 'posts' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />}
              </button>
            </div>

            <div className="pb-12">
              {activeTab === 'threads' ? (
                threads.length === 0 ? (
                  <p className="text-text-secondary text-center py-12">No threads yet</p>
                ) : (
                  <div className="space-y-3">
                    {threads.map((thread: any) => (
                      <a
                        key={thread.id}
                        href={`https://forum.outlawoasis.com/thread/${thread.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block bg-surface rounded-xl border border-gold/20 p-4 hover:border-gold/40 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-cream font-medium mb-1">{thread.title}</p>
                            <p className="text-xs text-text-muted">
                              in {thread.category?.name} · {new Date(thread.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-3 text-xs text-text-muted">
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" /> {thread.replyCount}
                            </span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )
              ) : (
                posts.length === 0 ? (
                  <p className="text-text-secondary text-center py-12">No replies yet</p>
                ) : (
                  <div className="space-y-3">
                    {posts.map((post: any) => (
                      <a
                        key={post.id}
                        href={`https://forum.outlawoasis.com/thread/${post.threadId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block bg-surface rounded-xl border border-gold/20 p-4 hover:border-gold/40 transition-colors"
                      >
                        <p className="text-sm text-gold mb-2">{post.thread?.title}</p>
                        <p className="text-text-secondary text-sm line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: post.content }}
                        />
                        <p className="text-xs text-text-muted mt-2">
                          {new Date(post.createdAt).toLocaleDateString()}
                        </p>
                      </a>
                    ))}
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
